import { useCallback, useState } from "react";
import classNames from "classnames";
import "moment-duration-format";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { useGetUserPlaylistClips } from "@ugg/shared/hooks/allstar";
import { useSummonerProfileContext } from "@ugg/shared/components/SummonerProfiles/SummonerProfileContext";
import { ReactComponent as AllstarLogo } from "@ugg/shared/assets/svg/allstar-logo.svg";
import { skipAllstar } from "@ugg/shared/api/requests/allstar/allstar-helpers";
import { AllstarTooltip } from "@ugg/shared/components/common/Allstar/AllstarTooltip";
import { ClipsPlaylistModal } from "@ugg/shared/components/SummonerProfiles/ClipsPlaylistModal";

export function ClipPlaylistBlock(props: { onClipOpen?: () => void }) {
  const { onClipOpen } = props;
  const { riotUserName, riotTagLine, region } = useSummonerProfileContext();
  const [modal, setModal] = useState(false);
  const skip = skipAllstar(region);
  const riotId = `${riotUserName}#${riotTagLine}`;

  const { data, loading } = useGetUserPlaylistClips(riotId, region);
  let { playlistUrl: playlistUrlRaw, fallback = true } = data?.data || {};
  if (skip) {
    return null;
  }

  // Allstar has 2 iframe layouts, need to request the smaller one
  const playlistUrl = `${playlistUrlRaw}&layout=nextUp`;

  const handleClick = useCallback(() => {
    if (!fallback) {
      setModal(true);
    }
  }, [fallback]);

  return (
    <div className="p-[18px] max-sm:p-[12px] rounded-[3px] bg-purple-400 bg-[linear-gradient(223deg,#313160_-134%,#191937_100%)]">
      <div className="flex items-center justify-between mb-[18px] max-sm:mb-[12px]">
        <HeaderBullet>
          <span className="whitespace-nowrap">Your Highlights</span>
        </HeaderBullet>
        <div className="flex items-center ml-[12px] h-0">
          <div className="flex items-end justify-end flex-wrap gap-y-[2px] whitespace-pre">
            <span className="leading-none text-[12px] font-medium text-lavender-50">Powered by</span>
            <span> </span>
            <AllstarTooltip>
              <div>
                <AllstarLogo className="flex-none w-[70px]" />
              </div>
            </AllstarTooltip>
          </div>
        </div>
      </div>
      <div className="relative w-full group/thumbnail flex items-center justify-center">
        <button
          onClick={handleClick}
          disabled={fallback}
          className={classNames("z-10 absolute w-full h-full hover:cursor-pointer bg-transparent", {
            hidden: fallback,
          })}
        />
        {!loading && playlistUrl && <iframe src={playlistUrl} allowFullScreen width={288} height={215} />}
      </div>
      {modal && !loading && playlistUrl && (
        <ClipsPlaylistModal
          className={classNames({
            "top-[var(--app-header-height)]": process.env.UGG_PUBLIC_PLATFORM === "app",
          })}
          skip={skip}
          src={playlistUrl}
          onOpen={() => onClipOpen?.()}
          onClose={() => setModal(false)}
        />
      )}
    </div>
  );
}
